import { deviceApi, fileStoreApi } from '../api';
import { Device, DeviceFileInfo, displayName, isDeviceOnline } from '../types';

export const FLEET_SEARCH_LIMIT = 30;

export type FleetEntry = {
  id: string;
  hostname: string;
  name?: string;
  model?: string;
  online: boolean;
  lastSeen?: number;
};

export function controllerFamily(hostname: string): string {
  const trimmed = (hostname || '').trim();
  const stripped = trimmed.replace(/[-_]?\d+$/, '');
  return stripped || trimmed || '—';
}

export function fleetLabel(entry: Pick<FleetEntry, 'id' | 'hostname' | 'name'>): string {
  return displayName({ id: entry.id, hostname: entry.hostname });
}

export function matchesFleetQuery(entry: FleetEntry, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) {
    return false;
  }
  const hay = `${entry.id} ${entry.hostname} ${entry.name || ''} ${entry.model || ''} ${controllerFamily(entry.hostname)}`.toLowerCase();
  return hay.includes(q);
}

export function filterFleet(entries: FleetEntry[], query: string, limit = FLEET_SEARCH_LIMIT): FleetEntry[] {
  return entries.filter((entry) => matchesFleetQuery(entry, query)).slice(0, limit);
}

export function mergeFleet(live: Device[], stored: DeviceFileInfo[]): FleetEntry[] {
  const byId = new Map<string, FleetEntry>();
  for (const device of stored) {
    if (!device.id) {
      continue;
    }
    byId.set(device.id, {
      id: device.id,
      hostname: device.hostname || device.id,
      name: device.name,
      model: device.model,
      online: false,
      lastSeen: device.lastSeen,
    });
  }
  for (const device of live) {
    const id = device.id || device.hostname;
    if (!id) {
      continue;
    }
    const prev = byId.get(id);
    byId.set(id, {
      id,
      hostname: device.hostname || prev?.hostname || id,
      name: device.name || prev?.name,
      model: device.model || prev?.model,
      online: isDeviceOnline(device),
      lastSeen: device.lastSeen ?? prev?.lastSeen,
    });
  }
  return Array.from(byId.values()).sort((a, b) => {
    if (a.online !== b.online) {
      return a.online ? -1 : 1;
    }
    return fleetLabel(a).localeCompare(fleetLabel(b), undefined, { sensitivity: 'base' });
  });
}

export async function loadFleet(): Promise<FleetEntry[]> {
  const [live, store] = await Promise.all([
    deviceApi.getDevices().catch(() => [] as Device[]),
    fileStoreApi.getStore().catch(() => ({ devices: [] as DeviceFileInfo[] })),
  ]);
  return mergeFleet(live, store.devices || []);
}
